/**
 * i18n configuration for react-i18next
 *
 * English translations are bundled eagerly (always needed as fallback).
 * Other languages are loaded on demand when the user switches language.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files - English only (always needed as fallback)
import commonEn from './locales/en/common.json';
import authEn from './locales/en/auth.json';
import tasksEn from './locales/en/tasks.json';
import userManagementEn from './locales/en/user_management.json';
import accountingEn from './locales/en/accounting.json';
import fileManagementEn from './locales/en/file_management.json';
import aiAgentsEn from './locales/en/aiAgents.json';
import pipelineEn from './locales/en/pipeline.json';
import emailEn from './locales/en/email.json';
import adminEn from './locales/en/admin.json';
import aiIntakeEn from './locales/en/ai_intake.json';
import sysadminEn from './locales/en/sysadmin.json';
import codingEn from './locales/en/coding.json';
import developerEn from './locales/en/developer.json';
import recruitingEn from './locales/en/recruiting.json';
import careersEn from './locales/en/careers.json';
import peopleEn from './locales/en/people.json';
import aiChatEn from './locales/en/aiChat.json';
import messagingEn from './locales/en/messaging.json';
import errorsEn from './locales/en/errors.json';
import messagesEn from './locales/en/messages.json';
import avatarsEn from './locales/en/avatars.json';
import productsEn from './locales/en/products.json';
import contactsEn from './locales/en/contacts.json';
import supportEn from './locales/en/support.json';
import externalEn from './locales/en/external.json';
import designSystemEn from './locales/en/design_system.json';
import websiteHostingEn from './locales/en/websiteHosting.json';
import shipmentsEn from './locales/en/shipments.json';
import customsEn from './locales/en/customs.json';
import formsBpmEn from './locales/en/formsBpm.json';
import remindersEn from './locales/en/reminders.json';
import inventoryEn from './locales/en/inventory.json';
import procurementEn from './locales/en/procurement.json';
import customFieldsEn from './locales/en/custom_fields.json';
import softwareFactoryEn from './locales/en/softwareFactory.json';
import notificationsEn from './locales/en/notifications.json';
import monitoringEn from './locales/en/monitoring.json';
import marketingEn from './locales/en/marketing.json';
import contractsEn from './locales/en/contracts.json';
import salesEn from './locales/en/sales.json';
import brokerageEn from './locales/en/brokerage.json';
import billingEn from './locales/en/billing.json';
import chatbotEn from './locales/en/chatbot.json';
import onboardingEn from './locales/en/onboarding.json';
import whatsappEn from './locales/en/whatsapp.json';

// Supported languages
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', dir: 'rtl' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr' },
] as const;

export type SupportedLanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

// Default language
export const DEFAULT_LANGUAGE: SupportedLanguageCode = 'en';

// Default terminology variables — resolved at runtime via tenant overrides.
// These EN defaults ensure strings render correctly even before the API loads.
// {{Product}} = title case, {{product}} = lowercase. Same pattern for all 11 terms.
// vendor_order decouples the variable name ({{VendorOrder}}) from its display
// default ("Vendor quote/order/pro-forma") via the config "variable" field.
// Exported so the errors.json placeholder-contract test can assert against the
// same set i18next is actually initialised with.
export const TERMINOLOGY_DEFAULT_VARIABLES: Record<string, string> = {
  Product: 'Product', Products: 'Products', product: 'product', products: 'products',
  Deal: 'Deal', Deals: 'Deals', deal: 'deal', deals: 'deals',
  Contact: 'Contact', Contacts: 'Contacts', contact: 'contact', contacts: 'contacts',
  Ticket: 'Ticket', Tickets: 'Tickets', ticket: 'ticket', tickets: 'tickets',
  Agent: 'Agent', Agents: 'Agents', agent: 'agent', agents: 'agents',
  Company: 'Company', Companies: 'Companies', company: 'company', companies: 'companies',
  Organization: 'Organization', Organizations: 'Organizations', organization: 'organization', organizations: 'organizations',
  Task: 'Task', Tasks: 'Tasks', task: 'task', tasks: 'tasks',
  Employee: 'Employee', Employees: 'Employees', employee: 'employee', employees: 'employees',
  Contract: 'Contract', Contracts: 'Contracts', contract: 'contract', contracts: 'contracts',
  VendorOrder: 'Vendor quote/order/pro-forma', VendorOrders: 'Vendor quotes/orders/pro-formas',
  vendorOrder: 'vendor quote/order/pro-forma', vendorOrders: 'vendor quotes/orders/pro-formas',
};

// Namespace list shared between init and lazy loaders.
// Exported so tests can assert every non-English loader covers all namespaces
// (guards against the loader-drift bug where a fully-translated namespace was
// never wired into the es/he lazy loaders and silently fell back to English).
export const ALL_NAMESPACES = ['common', 'auth', 'tasks', 'user_management', 'accounting', 'file_management', 'aiAgents', 'pipeline', 'email', 'admin', 'ai_intake', 'sysadmin', 'coding', 'developer', 'recruiting', 'careers', 'people', 'aiChat', 'messaging', 'errors', 'messages', 'avatars', 'products', 'contacts', 'support', 'external', 'design_system', 'websiteHosting', 'shipments', 'customs', 'formsBpm', 'reminders', 'inventory', 'procurement', 'softwareFactory', 'notifications', 'monitoring', 'marketing', 'contracts', 'sales', 'brokerage', 'billing', 'chatbot', 'onboarding', 'custom_fields', 'whatsapp'] as const;

export type I18nNamespace = (typeof ALL_NAMESPACES)[number];

/**
 * Shape every per-language resource module (resources.es.ts / resources.he.ts /
 * resources.zh.ts) must default-export: one bundle per namespace. Typing it as
 * an exact Record over ALL_NAMESPACES makes tsc fail the resource module itself
 * when a new namespace is added to ALL_NAMESPACES but not wired into a language
 * — compile-time protection on top of the languageLoaderCoverage runtime test.
 */
export type LanguageResourceBundles = Record<I18nNamespace, Record<string, unknown>>;

// Track which languages have been loaded
const loadedLanguages = new Set<string>(['en']);

// Languages served by a lazy per-language resource module (everything but the
// eagerly-bundled English).
const LAZY_LANGUAGES = new Set<string>(
  SUPPORTED_LANGUAGES.map((l) => l.code).filter((code) => code !== DEFAULT_LANGUAGE),
);

/**
 * Import a language's resource module (resources.es.ts / resources.he.ts /
 * resources.zh.ts — one bundle per namespace, see LanguageResourceBundles).
 *
 * The COMPUTED specifier is load-bearing (WI d36e25d5) — do not "simplify" it
 * to literal `import('./resources.es')` calls or an `import.meta.glob`:
 *
 * - vitest's `--changed` impact analysis follows LITERAL static and dynamic
 *   import specifiers. With literal per-file imports here, every es/he/zh
 *   locale JSON was a transitive dependency of config.ts — and config.ts is in
 *   virtually every component test's graph (via shared/utils/errors.ts etc.),
 *   so ANY non-EN locale edit dirtied ~940 of ~1190 test files in the
 *   merge-and-ship frontend gate. A computed specifier has no statically
 *   recordable target, so non-EN JSONs stay out of the test graph. English
 *   stays statically imported above — EN edits still (correctly) dirty their
 *   real importers.
 * - At build time Vite's built-in dynamic-import-vars support globs
 *   `./resources.*.ts` and emits one lazy chunk per language, so runtime
 *   behavior (all languages bundled and switchable) is unchanged. In dev and
 *   vitest the specifier resolves at runtime. The `.ts` extension is required
 *   for the glob (dynamic-import-vars needs an extension pattern).
 */
async function loadLanguageResources(lang: string): Promise<LanguageResourceBundles> {
  const mod = (await import(`./resources.${lang}.ts`)) as { default: LanguageResourceBundles };
  return mod.default;
}

/**
 * Load translations for a language on demand.
 * Uses i18n.addResourceBundle() to inject them at runtime.
 */
async function loadLanguageBundles(lang: string): Promise<void> {
  if (loadedLanguages.has(lang)) return;

  if (!LAZY_LANGUAGES.has(lang)) {
    // Mark as loaded even without a resource module to prevent infinite
    // re-trigger (e.g. LanguageDetector returns "en-US" which has no resource
    // module but isn't "en")
    loadedLanguages.add(lang);
    return;
  }

  const bundles = await loadLanguageResources(lang);
  for (const [ns, translations] of Object.entries(bundles)) {
    i18n.addResourceBundle(lang, ns, translations, true, true);
  }
  loadedLanguages.add(lang);
}

/**
 * Load translations for a language, then switch to it.
 * Call this instead of i18n.changeLanguage() to ensure translations
 * are available before the UI re-renders.
 */
export async function changeLanguage(lang: string): Promise<void> {
  await loadLanguageBundles(lang);
  await i18n.changeLanguage(lang);
}

/**
 * Name of BOTH the localStorage key and the cross-subdomain cookie that hold the
 * user's chosen UI language. localStorage is origin-scoped, so a language picked
 * or account-synced on `sysadmin.panluma.ai` is invisible to `app.panluma.ai` —
 * the single canonical Google/Microsoft SSO-callback host. That left the
 * post-SSO callback and the 2FA challenge it renders falling back to the
 * browser/GeoIP default (e.g. Spanish). A domain-scoped cookie is the one store
 * every `*.panluma.ai` subdomain shares, so it carries the known preference
 * across the SSO hop.
 */
export const LANGUAGE_STORAGE_KEY = 'panluma_language';
const LANGUAGE_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * Widen the language cookie to the registrable parent of any `*.panluma.ai` host
 * so `app` / `sysadmin` / tenant subdomains all share it. localhost and
 * fully-custom tenant domains stay host-scoped (no Domain attribute) — a custom
 * domain is a single host and over-widening to its public suffix would be
 * rejected by the browser.
 */
function languageCookieDomain(): string | undefined {
  const host = window.location.hostname;
  if (host === 'panluma.ai' || host.endsWith('.panluma.ai')) return '.panluma.ai';
  return undefined;
}

/** Read the cross-subdomain known-language cookie (undefined when unset). */
export function readKnownLanguageCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${LANGUAGE_STORAGE_KEY}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Persist a *known* language preference — the account `preferred_language` or an
 * explicit LanguageSelector choice — to the cross-subdomain cookie AND
 * localStorage. This is the single writer of the authoritative cookie: the
 * i18next detector caches only to localStorage (see `detection.caches`), so a
 * transient GeoIP guess on one subdomain never masquerades as a known preference
 * to the others. Once set, it is read ahead of localStorage/navigator on every
 * subdomain (and by useGeoLanguageDefault to suppress the GeoIP upgrade).
 */
export function persistKnownLanguage(lang: string): void {
  if (typeof document === 'undefined') return;
  // Defense-in-depth: only ever persist a supported code. Every current caller
  // already passes a validated code, but this guarantees an unsupported value can
  // never reach the cross-subdomain cookie via a future caller.
  if (!SUPPORTED_LANGUAGES.some((l) => l.code === lang)) return;
  const domain = languageCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${LANGUAGE_STORAGE_KEY}=${encodeURIComponent(lang)}` +
    `; Max-Age=${LANGUAGE_COOKIE_MAX_AGE_SECONDS}; Path=/` +
    `${domain ? `; Domain=${domain}` : ''}; SameSite=Lax${secure}`;
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
}

/**
 * Expire the cross-subdomain known-language cookie. Used when ending sysadmin
 * emulation: the emulated user's language was written cross-subdomain during the
 * session and must not leak onto the sysadmin's own session after restore. Match
 * the same Domain/Path so the browser actually removes it.
 */
export function clearKnownLanguageCookie(): void {
  if (typeof document === 'undefined') return;
  const domain = languageCookieDomain();
  // Deletion matches on Name+Domain+Path only, but keep SameSite for symmetry with
  // persistKnownLanguage so the cookie format stays the canonical one.
  document.cookie =
    `${LANGUAGE_STORAGE_KEY}=; Max-Age=0; Path=/${domain ? `; Domain=${domain}` : ''}; SameSite=Lax`;
}

// English resources bundled eagerly
const resources = {
  en: {
    common: commonEn,
    auth: authEn,
    tasks: tasksEn,
    user_management: userManagementEn,
    accounting: accountingEn,
    file_management: fileManagementEn,
    aiAgents: aiAgentsEn,
    pipeline: pipelineEn,
    email: emailEn,
    admin: adminEn,
    ai_intake: aiIntakeEn,
    sysadmin: sysadminEn,
    coding: codingEn,
    developer: developerEn,
    recruiting: recruitingEn,
    careers: careersEn,
    people: peopleEn,
    aiChat: aiChatEn,
    messaging: messagingEn,
    errors: errorsEn,
    messages: messagesEn,
    avatars: avatarsEn,
    products: productsEn,
    contacts: contactsEn,
    support: supportEn,
    external: externalEn,
    design_system: designSystemEn,
    websiteHosting: websiteHostingEn,
    shipments: shipmentsEn,
    customs: customsEn,
    formsBpm: formsBpmEn,
    reminders: remindersEn,
    inventory: inventoryEn,
    procurement: procurementEn,
    custom_fields: customFieldsEn,
    softwareFactory: softwareFactoryEn,
    notifications: notificationsEn,
    monitoring: monitoringEn,
    marketing: marketingEn,
    contracts: contractsEn,
    sales: salesEn,
    brokerage: brokerageEn,
    billing: billingEn,
    chatbot: chatbotEn,
    onboarding: onboardingEn,
    whatsapp: whatsappEn,
  },
};

// Initialize i18next with English only
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    // Pin the accepted language set to the single source of truth. Without this
    // the LanguageDetector would set i18n.language to any detected code (e.g. a
    // browser/GeoIP "fr" we don't translate, or "en-GB"); `nonExplicitSupportedLngs`
    // folds region variants ("en-US" -> "en") onto a supported base so the lazy
    // loader and dir/RTL lookups always see a known code.
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    ns: [...ALL_NAMESPACES],
    interpolation: {
      escapeValue: false, // React already escapes values
      defaultVariables: TERMINOLOGY_DEFAULT_VARIABLES,
    },
    detection: {
      // `querystring` first so a pre-auth page reached from an account email link
      // (reset-password, verify-email, /join, external portals) renders in the
      // language the email was sent in. The backend appends `?lang=<preferred>` to
      // those links (shared/utils/frontend_url.py).
      //
      // `cookie` next: the cross-subdomain known-language cookie (see
      // persistKnownLanguage). localStorage is origin-scoped, so when Google/MS SSO
      // redirects from `sysadmin.panluma.ai` to the canonical callback host
      // `app.panluma.ai`, the language chosen on sysadmin lives in a different
      // origin's localStorage. The cookie carries it across the hop so the SSO
      // callback + 2FA challenge render in the most-recently-signed-in user's
      // language. Without a session the app would otherwise fall back to the
      // browser/GeoIP default. Once authenticated, UserLanguageSync re-asserts the
      // account's preferred_language. `caches` stays localStorage-only — the cookie
      // is written exclusively by persistKnownLanguage so GeoIP guesses never
      // pollute it.
      order: ['querystring', 'cookie', 'localStorage', 'navigator'],
      lookupQuerystring: 'lang',
      lookupCookie: LANGUAGE_STORAGE_KEY,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false, // Disable suspense for simplicity
    },
  });

// Safety net: if someone calls i18n.changeLanguage() directly, still load bundles.
// The UI will briefly show English fallback keys then re-render once bundles arrive.
// Prefer using the exported changeLanguage() wrapper to avoid this flash.
i18n.on('languageChanged', (lang) => {
  if (lang !== 'en' && !loadedLanguages.has(lang)) {
    loadLanguageBundles(lang).then(() => {
      // Force re-render after bundles are injected
      i18n.emit('languageChanged', lang);
    });
  }
});

// If the detected language at startup is not English, load it immediately
if (i18n.language && i18n.language !== 'en') {
  loadLanguageBundles(i18n.language).then(() => {
    // Re-trigger so react-i18next re-renders with the loaded translations
    i18n.changeLanguage(i18n.language);
  });
}

/**
 * Apply tenant i18n overrides fetched from the API.
 * Called by the TerminologyProvider after loading tenant data.
 */
export function applyTenantI18nOverrides(
  locale: string,
  terms: Record<string, string>,
  overrides: Array<{ string_key: string; override_value: string }>,
): void {
  // Set curated terms as default interpolation variables
  // e.g., {{product}} will resolve to "Module" if tenant overrode "Product" -> "Module"
  i18n.options.interpolation = {
    ...i18n.options.interpolation,
    defaultVariables: {
      ...i18n.options.interpolation?.defaultVariables,
      ...terms,
    },
  };

  // Apply arbitrary per-key string overrides (escape hatch)
  if (Array.isArray(overrides)) {
    for (const { string_key, override_value } of overrides) {
      const colonIdx = string_key.indexOf(':');
      if (colonIdx > 0) {
        const namespace = string_key.substring(0, colonIdx);
        const key = string_key.substring(colonIdx + 1);
        i18n.addResource(locale, namespace, key, override_value);
      }
    }
  }

  // Force re-render so components pick up the new defaultVariables
  i18n.emit('languageChanged', i18n.language);
}

export default i18n;
